// Martian identity: wordmark, lockup, and the single <MartianLogo/> facade
// the app shells use.
//
//   Wordmark  -- "MARTIAN" in Inter Tight Black (live text, themeable).
//   Lockup    -- GLIM + wordmark (+ optional "AI Chief Growth Officer").
//   MartianLogo variants: 'lockup' | 'wordmark' | 'glim'.
//
// GLIM is the only character ever paired with the wordmark. ARC is never
// part of the corporate lockup. Positioning copy comes from BRAND_COPY
// ("MARTIAN / AI Chief Growth Officer"); no other taglines are invented.

import { GLIM } from './GLIM';
import { BRAND_COPY } from './assets';
import './brand.css';

const SCALE = {
  sm: { glim: 24, word: 14, desc: 10, gap: 8 },
  md: { glim: 32, word: 18, desc: 11, gap: 10 },
  lg: { glim: 48, word: 28, desc: 13, gap: 14 },
  xl: { glim: 72, word: 40, desc: 16, gap: 18 },
};

export function MartianWordmark({ size = 'md', tone = 'light', className = '', ...rest }) {
  const s = SCALE[size] || SCALE.md;
  return (
    <span
      className={`martian-wordmark ${tone === 'dark' ? 'martian-lockup--dark' : ''} ${className}`.trim()}
      style={{ fontSize: s.word }}
      data-testid="martian-wordmark"
      {...rest}
    >
      {BRAND_COPY.name}
    </span>
  );
}

export function MartianLockup({
  size = 'md',
  tone = 'light',
  layout = 'horizontal',
  showDescriptor = false,
  className = '',
  ...rest
}) {
  const s = SCALE[size] || SCALE.md;
  const stacked = layout === 'stacked';
  return (
    <span
      className={`martian-lockup ${tone === 'dark' ? 'martian-lockup--dark' : ''} ${className}`.trim()}
      style={{
        display: 'inline-flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'center',
        gap: s.gap,
        textAlign: stacked ? 'center' : 'start',
      }}
      data-testid="martian-lockup"
      {...rest}
    >
      <GLIM size={s.glim * (stacked ? 1.6 : 1)} tone={tone === 'dark' ? 'cream-on-ink' : 'clay'} decorative />
      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, alignItems: stacked ? 'center' : 'flex-start' }}>
        <MartianWordmark size={size} tone={tone} />
        {showDescriptor && (
          <span className="martian-lockup__descriptor" style={{ fontSize: s.desc }}>
            {BRAND_COPY.descriptor}
          </span>
        )}
      </span>
    </span>
  );
}

export function MartianLogo({
  variant = 'lockup',
  size = 'md',
  tone = 'light',
  showDescriptor = false,
  className = '',
  ...rest
}) {
  if (variant === 'glim') {
    const s = SCALE[size] || SCALE.md;
    return (
      <GLIM
        size={s.glim}
        tone={tone === 'dark' ? 'cream-on-ink' : 'clay'}
        label="Martian"
        className={className}
        {...rest}
      />
    );
  }
  if (variant === 'wordmark') {
    return <MartianWordmark size={size} tone={tone} className={className} {...rest} />;
  }
  return <MartianLockup size={size} tone={tone} showDescriptor={showDescriptor} className={className} {...rest} />;
}

export default MartianLogo;
